import React, { useRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import NotificationModal from '../components/NotificationModal'
import { updateProfile, updateProfilePicture, signOut, setBusinessLogo, fetchProfile } from '../store/slices/authSlice'
import { mediaAPI } from '../services/(adminapi)/mediaAPI'
import { resolveMediaUrl } from '../utils/mediaUrl'

const UserIcon = () => (
  <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
)

const BuildingIcon = () => (
  <svg width="48" height="48" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
  </svg>
)

export default function Profile() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, business } = useSelector((s) => s.auth)
  const profilePicRef = useRef(null)
  const businessLogoRef = useRef(null)
  const [name, setName] = useState(user?.name ?? '')
  const [savingName, setSavingName] = useState(false)
  const [uploadingPic, setUploadingPic] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    dispatch(fetchProfile())
  }, [dispatch])

  useEffect(() => {
    setName(user?.name ?? '')
  }, [user?.name])

  const handleSignOut = async () => {
    try {
      await dispatch(signOut()).unwrap()
      navigate('/signin')
    } catch (e) {
      console.error('Sign out failed', e)
    }
  }

  const handleSaveName = async () => {
    const trimmed = (name || '').trim()
    if (trimmed === (user?.name ?? '')) return
    setSavingName(true)
    try {
      await dispatch(updateProfile({ name: trimmed })).unwrap()
      setSuccessMessage('Profile updated successfully')
    } catch (e) {
      console.error('Failed to update name', e)
    } finally {
      setSavingName(false)
    }
  }

  const handleProfilePicChange = (e) => {
    const file = e.target.files?.[0]
    if (!file?.type.startsWith('image/')) return
    setUploadingPic(true)
    const formData = new FormData()
    formData.append('file', file)
    mediaAPI.uploadProfilePicture(formData)
      .then((res) => {
        const url = res.data?.data?.url || res.data?.data?.profile_picture
        if (url) {
          dispatch(updateProfilePicture.fulfilled(`${resolveMediaUrl(url)}?t=${Date.now()}`))
          setSuccessMessage('Profile picture uploaded successfully')
        }
      })
      .catch((err) => console.error('Profile picture update failed:', err))
      .finally(() => {
        setUploadingPic(false)
        e.target.value = ''
        profilePicRef.current && (profilePicRef.current.value = '')
      })
  }

  const handleBusinessLogoChange = (e) => {
    const file = e.target.files?.[0]
    if (!file?.type.startsWith('image/')) return
    setUploadingLogo(true)
    const formData = new FormData()
    formData.append('file', file)
    mediaAPI.uploadBusinessLogo(formData)
      .then((res) => {
        const url = res.data?.data?.url || res.data?.data?.logo
        if (url) {
          dispatch(setBusinessLogo(`${resolveMediaUrl(url)}?t=${Date.now()}`))
          setSuccessMessage('Business logo uploaded successfully')
        }
      })
      .catch((err) => console.error('Business logo update failed:', err))
      .finally(() => {
        setUploadingLogo(false)
        e.target.value = ''
        businessLogoRef.current && (businessLogoRef.current.value = '')
      })
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex',
  }
  const mainStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary,
  }
  const contentWrapStyle = {
    width: '100%',
    maxWidth: 1040,
    margin: '0 auto',
  }
  const introCardStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: 12,
    border: `1px solid ${colors.borderVariants.light}`,
    padding: 20,
    marginBottom: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  }
  const introLeftStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  }
  const introTitleStyle = {
    fontSize: 18,
    fontWeight: 700,
    color: colors.text.primary,
    margin: 0,
    lineHeight: 1.2,
  }
  const introSubtitleStyle = {
    fontSize: 13,
    color: colors.text.secondary,
    margin: 0,
  }
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr',
    gap: 18,
    alignItems: 'start',
  }
  const cardStyle = {
    backgroundColor: colors.background.primary,
    borderRadius: 8,
    border: `1px solid ${colors.borderVariants.light}`,
    padding: 24,
    marginBottom: 24,
  }
  const cardHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  }
  const cardTitleWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  }
  const sectionTitleStyle = {
    fontSize: 16,
    fontWeight: 600,
    color: colors.text.primary,
    margin: 0,
  }
  const sectionSubtitleStyle = {
    fontSize: 12,
    color: colors.text.secondary,
    margin: 0,
  }
  const labelStyle = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.text.secondary,
    marginBottom: 6,
    display: 'block',
  }
  const inputStyle = {
    width: '100%',
    maxWidth: 400,
    padding: '10px 12px',
    fontSize: 14,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: 6,
    color: colors.text.primary,
    boxSizing: 'border-box',
  }
  const readOnlyStyle = {
    ...inputStyle,
    backgroundColor: colors.background.tertiary,
    color: colors.text.secondary,
  }
  const btnStyle = {
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    marginTop: 8,
  }
  const btnSecondaryStyle = {
    ...btnStyle,
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
  }
  const btnDisabledStyle = {
    opacity: 0.6,
    cursor: 'not-allowed',
  }
  const avatarWrap = {
    width: 96,
    height: 96,
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: colors.background.tertiary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.text.light,
  }
  const logoWrap = {
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.background.tertiary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.text.light,
  }

  const backBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    marginBottom: 20,
    fontSize: 14,
    fontWeight: 500,
    color: colors.text.primary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: 6,
    cursor: 'pointer',
  }

  return (
    <div style={containerStyle}>
      <NotificationModal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        type="success"
        title="Success"
        message={successMessage || ''}
        showOverlay={false}
      />
      <Sidebar />
      <main style={mainStyle}>
        <PageTopBar title="Profile" onSignOut={handleSignOut} />
        <div style={contentWrapStyle}>
          <div style={introCardStyle}>
            <div style={introLeftStyle}>
              <h2 style={introTitleStyle}>Account & business profile</h2>
              <p style={introSubtitleStyle}>Manage your personal details and business branding.</p>
            </div>
            <button type="button" style={backBtnStyle} onClick={() => navigate(-1)} aria-label="Go back">
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>
          </div>

          <div style={gridStyle}>
            {/* Your profile */}
            <div style={{ ...cardStyle, marginBottom: 0 }}>
              <div style={cardHeaderStyle}>
                <div style={cardTitleWrapStyle}>
                  <h3 style={sectionTitleStyle}>Your profile</h3>
                  <p style={sectionSubtitleStyle}>Update your display name and profile picture.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
                <div>
                  <div style={avatarWrap}>
                    {user?.profile_picture ? (
                      <img src={resolveMediaUrl(user.profile_picture)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <UserIcon />
                    )}
                  </div>
                  <input
                    ref={profilePicRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePicChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    style={{ ...btnSecondaryStyle, marginTop: 12, ...(uploadingPic ? btnDisabledStyle : null) }}
                    disabled={uploadingPic}
                    onClick={() => profilePicRef.current?.click()}
                  >
                    {uploadingPic ? 'Uploading…' : 'Change picture'}
                  </button>
                </div>

                <div style={{ flex: 1, minWidth: 240 }}>
                  <label style={labelStyle}>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    style={inputStyle}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={handleSaveName}
                      disabled={savingName || ((name || '').trim() === (user?.name ?? ''))}
                      style={{
                        ...btnStyle,
                        marginTop: 0,
                        ...(savingName || ((name || '').trim() === (user?.name ?? '')) ? btnDisabledStyle : null),
                      }}
                    >
                      {savingName ? 'Saving…' : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setName(user?.name ?? '')}
                      disabled={savingName || name === (user?.name ?? '')}
                      style={{
                        ...btnSecondaryStyle,
                        marginTop: 0,
                        ...(savingName || name === (user?.name ?? '') ? btnDisabledStyle : null),
                      }}
                    >
                      Reset
                    </button>
                  </div>

                  <label style={{ ...labelStyle, marginTop: 16 }}>Email</label>
                  <input
                    type="text"
                    value={user?.email ?? ''}
                    readOnly
                    style={readOnlyStyle}
                    title="Email cannot be changed"
                  />
                </div>
              </div>
            </div>

            {/* Business info */}
            <div style={{ ...cardStyle, marginBottom: 0 }}>
              <div style={cardHeaderStyle}>
                <div style={cardTitleWrapStyle}>
                  <h3 style={sectionTitleStyle}>Business</h3>
                  <p style={sectionSubtitleStyle}>Branding used across your workspace.</p>
                </div>
              </div>

              <label style={labelStyle}>Business name</label>
              <input type="text" value={business?.name ?? ''} readOnly style={readOnlyStyle} />

              <div style={{ marginTop: 16 }}>
                <label style={labelStyle}>Business logo</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={logoWrap}>
                    {business?.logo ? (
                      <img src={resolveMediaUrl(business.logo)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <BuildingIcon />
                    )}
                  </div>
                  <input
                    ref={businessLogoRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBusinessLogoChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    style={{ ...btnSecondaryStyle, marginTop: 0, ...(uploadingLogo ? btnDisabledStyle : null) }}
                    disabled={uploadingLogo}
                    onClick={() => businessLogoRef.current?.click()}
                  >
                    {uploadingLogo ? 'Uploading…' : 'Change logo'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
